/**
 * Reading a `timeslots[]` array the way fungus means it.
 *
 * Both the overlay (`js/data/schedule.js`) and the reveal pages (`js/reveal-shared.js`)
 * need these, and both used to carry their own copy — with a comment in each pointing at
 * the other. One copy now: the two disagreeing is exactly the bug that produces a
 * schedule that is right on one page and wrong on the next.
 */

/**
 * Timeslots are a **linked list**, not an ordered array: the head has
 * `previous_timeslot_id: null`, and the next slot is whichever one points back at the
 * current id. Never sort by id or by date — the API returns them by primary key, and a
 * slot moved in the admin keeps its id.
 *
 * Slots unreachable from the head (a dangling `previous_timeslot_id`) would otherwise
 * vanish, so they are appended at the end. `onOrphans` gets them when there are any; the
 * overlay logs a warning, the reveal pages stay quiet.
 */
export function orderTimeslots(timeslots, onOrphans) {
    const byPrevious = new Map(timeslots.map((t) => [t.previous_timeslot_id ?? null, t]));

    const ordered = [];
    const seen = new Set();
    let node = byPrevious.get(null);
    while (node && !seen.has(node.id)) {
        seen.add(node.id);
        ordered.push(node);
        node = byPrevious.get(node.id);
    }

    const orphans = timeslots.filter((t) => !seen.has(t.id));
    if (orphans.length && onOrphans) onOrphans(orphans);

    return ordered.concat(orphans);
}

/**
 * When a run actually starts. `projected_start` has the overruns of every earlier slot
 * already folded in by the backend; `start_date` / `date` are the original plan and are
 * only a fallback for a slot orphaned from the chain, where `projected_start` is null.
 */
export const getStartDate = (timeslot) =>
    timeslot?.projected_start ?? timeslot?.start_date ?? timeslot?.date;

/**
 * When the run itself starts, after its setup: the slot opens at `getStartDate()` with
 * `setup_time` seconds of setup, and the game only starts once that is over. This is the
 * time a published schedule shows. A slot with no `setup_time` starts at its slot start.
 */
export const getRunStartDate = (timeslot) => {
    const slotStart = getStartDate(timeslot);
    if (!slotStart) return slotStart;
    return new Date(new Date(slotStart).getTime() + (timeslot.setup_time ?? 0) * 1000);
};
